import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../../../utils/api';
import { useNavigate } from 'react-router-dom';
import { 
    Calendar as CalendarIcon, 
    Settings, 
    Bell, 
    ChevronLeft, 
    ChevronRight, 
    ExternalLink,
    Filter,
    Search
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../components/ui/utils';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../../../components/ui/select';
import { toast } from 'sonner';

// Setup the localizer for react-big-calendar
const localizer = momentLocalizer(moment);

interface CalendarEvent {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    type: 'maintenance' | 'reminder';
    status: string;
    assetId: number;
    description?: string;
}

const MaintenanceCalendar: React.FC = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<any>('month');
    const [date, setDate] = useState(new Date());

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const data = await api.get<any[]>('/api/assets/calendar-events');
            
            const formattedEvents = data.map(event => ({
                ...event,
                start: new Date(event.start),
                end: new Date(event.end),
            }));
            
            setEvents(formattedEvents);
        } catch (error) {
            console.error('Error fetching calendar events:', error);
            toast.error('Failed to load maintenance events');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const handleSelectEvent = (event: CalendarEvent) => {
        if (event.type === 'maintenance') {
            const maintenanceId = event.id.replace('m-', '');
            navigate(`/assets/${event.assetId}/maintenance/${maintenanceId}`);
        } else {
            navigate(`/assets/${event.assetId}?tab=reminders`);
        }
    };

    const eventStyleGetter = (event: CalendarEvent) => {
        let backgroundColor = '#3b82f6'; // Default blue
        let borderColor = '#2563eb';

        if (event.type === 'reminder') {
            backgroundColor = '#f59e0b'; // Amber
            borderColor = '#d97706';
        }

        if (event.status === 'In Progress') {
            backgroundColor = '#10b981'; // Emerald
            borderColor = '#059669';
        }

        const style = {
            backgroundColor,
            borderRadius: '10px',
            opacity: 0.9,
            color: 'white',
            border: `1px solid ${borderColor}`,
            display: 'block',
            fontSize: '11px',
            fontWeight: '600',
            padding: '4px 8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        };
        return { style };
    };

    const CustomToolbar = (toolbar: any) => {
        const goToBack = () => {
            toolbar.onNavigate('PREV');
            setDate(moment(date).subtract(1, view === 'month' ? 'month' : 'week').toDate());
        };

        const goToNext = () => {
            toolbar.onNavigate('NEXT');
            setDate(moment(date).add(1, view === 'month' ? 'month' : 'week').toDate());
        };

        const goToCurrent = () => {
            toolbar.onNavigate('TODAY');
            setDate(new Date());
        };

        return (
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8 bg-white p-4 rounded-[24px] border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2">
                    <div className="p-2.5 bg-blue-50 rounded-xl">
                        <CalendarIcon className="text-blue-500" size={20} />
                    </div>
                    <div>
                        <h2 className="text-lg font-black text-gray-900 leading-tight">
                            {moment(toolbar.date).format('MMMM YYYY')}
                        </h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            {view} view • {(() => {
                                const start = moment(toolbar.date).startOf(view === 'agenda' ? 'month' : view).startOf('week');
                                const end = moment(toolbar.date).endOf(view === 'agenda' ? 'month' : view).endOf('week');
                                return events.filter(e => moment(e.start).isBetween(start, end, null, '[]')).length;
                            })()} Events
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Select
                        value={moment(toolbar.date).month().toString()}
                        onValueChange={(val) => {
                            const newDate = moment(toolbar.date).month(parseInt(val)).toDate();
                            toolbar.onNavigate('DATE', newDate);
                            setDate(newDate);
                        }}
                    >
                        <SelectTrigger className="w-[130px] rounded-xl h-10 border-gray-100 bg-gray-50/50 font-bold text-xs">
                            <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-gray-100">
                            {moment.months().map((month, index) => (
                                <SelectItem key={month} value={index.toString()} className="text-xs font-bold py-2">
                                    {month}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={moment(toolbar.date).year().toString()}
                        onValueChange={(val) => {
                            const newDate = moment(toolbar.date).year(parseInt(val)).toDate();
                            toolbar.onNavigate('DATE', newDate);
                            setDate(newDate);
                        }}
                    >
                        <SelectTrigger className="w-[100px] rounded-xl h-10 border-gray-100 bg-gray-50/50 font-bold text-xs">
                            <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-gray-100 max-h-[300px]">
                            {Array.from({ length: 16 }, (_, i) => moment().year() - 5 + i).map((year) => (
                                <SelectItem key={year} value={year.toString()} className="text-xs font-bold py-2">
                                    {year}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center gap-1.5 p-1 bg-gray-50 rounded-xl border border-gray-100">
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        className={cn("rounded-lg h-8 text-xs font-bold transition-all", view === 'month' ? "bg-white shadow-sm text-slate-900" : "text-gray-500")}
                        onClick={() => { setView('month'); toolbar.onView('month'); }}
                    >
                        Month
                    </Button>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        className={cn("rounded-lg h-8 text-xs font-bold transition-all", view === 'week' ? "bg-white shadow-sm text-slate-900" : "text-gray-500")}
                        onClick={() => { setView('week'); toolbar.onView('week'); }}
                    >
                        Week
                    </Button>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        className={cn("rounded-lg h-8 text-xs font-bold transition-all", view === 'agenda' ? "bg-white shadow-sm text-slate-900" : "text-gray-500")}
                        onClick={() => { setView('agenda'); toolbar.onView('agenda'); }}
                    >
                        Agenda
                    </Button>
                </div>

                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={goToCurrent}
                        className="rounded-xl h-10 border-gray-100 font-bold text-xs px-4"
                    >
                        Today
                    </Button>
                    <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-xl border border-gray-100">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={goToBack}
                            className="h-9 w-9 rounded-lg text-gray-600 hover:bg-white hover:shadow-sm"
                        >
                            <ChevronLeft size={18} />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={goToNext}
                            className="h-9 w-9 rounded-lg text-gray-600 hover:bg-white hover:shadow-sm"
                        >
                            <ChevronRight size={18} />
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-700">
            {/* Legend */}
            <div className="flex flex-wrap gap-4 px-2">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500 shadow-sm shadow-blue-200"></div>
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mt-0.5">Scheduled Maintenance</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-sm shadow-emerald-200"></div>
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mt-0.5">In Progress</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500 shadow-sm shadow-amber-200"></div>
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mt-0.5">Reminders</span>
                </div>
            </div>

            <div className="bg-white rounded-[32px] p-6 border border-gray-100 shadow-sm overflow-hidden h-[800px]">
                {loading ? (
                    <div className="h-full flex items-center justify-center">
                        <div className="flex flex-col items-center gap-4">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                            <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">Loading Calendar...</p>
                        </div>
                    </div>
                ) : (
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: '100%' }}
                        onSelectEvent={handleSelectEvent}
                        eventPropGetter={eventStyleGetter}
                        components={{
                            toolbar: CustomToolbar,
                        }}
                        views={['month', 'week', 'agenda']}
                        onView={(newView) => setView(newView)}
                        onNavigate={(newDate) => setDate(newDate)}
                        date={date}
                        popup
                    />
                )}
            </div>

            <style>{`
                .rbc-calendar {
                    font-family: inherit;
                }
                .rbc-month-view {
                    border-radius: 20px;
                    border: 1px solid #f3f4f6;
                    overflow: hidden;
                    background: #fff;
                }
                .rbc-header {
                    padding: 12px 10px !important;
                    font-size: 11px !important;
                    font-weight: 800 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                    color: #9ca3af !important;
                    border-bottom: 1px solid #f3f4f6 !important;
                    background: #f9fafb !important;
                }
                .rbc-month-row {
                    border-bottom: 1px solid #f3f4f6 !important;
                }
                .rbc-day-bg + .rbc-day-bg {
                    border-left: 1px solid #f3f4f6 !important;
                }
                .rbc-today {
                    background-color: #f8fafc !important;
                }
                .rbc-off-range-bg {
                    background-color: #fafafa !important;
                }
                .rbc-event {
                    transition: all 0.2s ease;
                }
                .rbc-event:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                }
                .rbc-show-more {
                    font-size: 10px !important;
                    font-weight: 800 !important;
                    text-transform: uppercase !important;
                    color: #3b82f6 !important;
                    background: transparent !important;
                }
                .rbc-agenda-view table {
                    border: 1px solid #f3f4f6 !important;
                    border-radius: 20px !important;
                    overflow: hidden !important;
                }
                .rbc-agenda-view table thead tr th {
                    background: #f9fafb !important;
                    padding: 15px !important;
                    font-size: 11px !important;
                    font-weight: 800 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                    color: #9ca3af !important;
                    text-align: left !important;
                }
                .rbc-agenda-date-cell, .rbc-agenda-time-cell, .rbc-agenda-event-cell {
                    padding: 15px !important;
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    color: #374151 !important;
                    border-bottom: 1px solid #f3f4f6 !important;
                }
            `}</style>
        </div>
    );
};

export default MaintenanceCalendar;
